'use client';

import { useEffect, useRef } from 'react';

type UseUrlChangeCallback = (url: string) => void;

export function useUrlChange(callback: UseUrlChangeCallback) {
  // Use useRef to store the latest callback without changing effect dependencies
  const callbackRef = useRef(callback);

  // Update the ref whenever the callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Effect to handle URL changes
  useEffect(() => {
    if (typeof window === 'undefined') {
      // Return an empty cleanup function for SSR
      return () => {};
    }

    let lastUrl = window.location.href;

    const observer = () => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        // Use setTimeout to ensure the callback is executed after the URL change
        // Prevent useInsertEffect from being called immediately (some library is using it)
        setTimeout(() => {
          // Use the ref to ensure the latest callback is always called
          if (callbackRef.current) {
            callbackRef.current(currentUrl);
          }
        }, 0);
      }
    };

    // Patch window.history.pushState and replaceState to trigger a custom event
    const patchHistoryMethod = (type: 'pushState' | 'replaceState') => {
      const original = window.history[type];
      window.history[type] = function (
        this: History,
        state: Record<string, unknown> | null, // state can be an object or null
        title: string, // 'title' is a string
        url: string, // 'url' is a string
      ) {
        const result = original.apply(this, [state, title, url]);
        window.dispatchEvent(new Event('urlchange'));
        return result;
      };
    };

    patchHistoryMethod('pushState');
    patchHistoryMethod('replaceState');

    // Listen for our custom event + back/forward navigation
    window.addEventListener('urlchange', observer);
    window.addEventListener('popstate', observer);

    // Also poll every 100ms in case of missed events
    const interval = setInterval(observer, 100);

    // Return the cleanup function
    return () => {
      window.removeEventListener('urlchange', observer);
      window.removeEventListener('popstate', observer);
      clearInterval(interval);
    };
  }, []);
}
